/**
 * Feeding baseline (spec 7.1): recency-weighted empirical feed intervals.
 *
 * Feeds closer together than `mergeWithinMin` are one feeding session (top-ups
 * and accidental double entries), so their zero/short gaps never train the
 * model. Gaps longer than `maxGapMin` are treated as untracked time and also
 * excluded. Original records are never modified.
 */
import type { LocalEvent } from '@/lib/schemas';
import { localParts } from '@/lib/time';
import { DAY_MS, MINUTE_MS, asOf, recencyWeight, toWindow, windowStats, type PredictionWindow, type Sample } from './common';

export const FEED_MODEL_VERSION = 'feed-baseline-v1';

export interface FeedingOptions {
  lookbackDays: number;
  halfLifeDays: number;
  /** Minimum valid intervals before any estimate is shown (spec: initially 10). */
  minIntervals: number;
  mergeWithinMin: number;
  maxGapMin: number;
  /** Local hours [nightStartHour, 24) and [0, nightEndHour) count as night. */
  nightStartHour: number;
  nightEndHour: number;
  lowerQ: number;
  upperQ: number;
}

export const DEFAULT_FEEDING_OPTIONS: FeedingOptions = {
  lookbackDays: 21,
  halfLifeDays: 7,
  minIntervals: 10,
  mergeWithinMin: 10,
  maxGapMin: 8 * 60,
  nightStartHour: 19,
  nightEndHour: 7,
  lowerQ: 0.2,
  upperQ: 0.8,
};

export type DayPart = 'day' | 'night';

export interface FeedSession {
  /** First feed of the session; its id/time anchor predictions. */
  id: string;
  startMs: number;
}

export interface FeedInterval {
  fromMs: number;
  toMs: number;
  minutes: number;
  part: DayPart;
}

export type FeedingPrediction =
  | {
      status: 'ok';
      modelVersion: string;
      basisEventId: string;
      basisMs: number;
      window: PredictionWindow;
      medianMin: number;
      /** Valid intervals in the group actually used. */
      sampleSize: number;
      group: DayPart | 'all';
      widened: boolean;
      overdue: boolean;
    }
  | {
      status: 'insufficient';
      modelVersion: string;
      sampleSize: number;
      required: number;
      reason: string;
    };

export function dayPart(ms: number, tz: string, opts: Pick<FeedingOptions, 'nightStartHour' | 'nightEndHour'>): DayPart {
  const h = localParts(ms, tz).hour;
  return h >= opts.nightStartHour || h < opts.nightEndHour ? 'night' : 'day';
}

/** Live formula feeds merged into sessions, oldest first. */
export function feedSessions(events: LocalEvent[], mergeWithinMin = DEFAULT_FEEDING_OPTIONS.mergeWithinMin): FeedSession[] {
  const feeds = events
    .filter((e) => e.type === 'formula' && !e.deleted_at)
    .sort((a, b) => a.started_ms - b.started_ms || a.id.localeCompare(b.id));
  const sessions: FeedSession[] = [];
  let lastMs = -Infinity;
  for (const f of feeds) {
    // Chain merging: each feed joins the session if it follows the previous feed closely.
    if (sessions.length && f.started_ms - lastMs <= mergeWithinMin * MINUTE_MS) {
      lastMs = f.started_ms;
      continue;
    }
    sessions.push({ id: f.id, startMs: f.started_ms });
    lastMs = f.started_ms;
  }
  return sessions;
}

/** Valid intervals between consecutive sessions, tagged by the local day part of their start. */
export function feedIntervals(sessions: FeedSession[], tz: string, opts: FeedingOptions = DEFAULT_FEEDING_OPTIONS): FeedInterval[] {
  const out: FeedInterval[] = [];
  for (let i = 1; i < sessions.length; i++) {
    const minutes = (sessions[i].startMs - sessions[i - 1].startMs) / MINUTE_MS;
    if (!(minutes > 0) || minutes > opts.maxGapMin) continue;
    out.push({ fromMs: sessions[i - 1].startMs, toMs: sessions[i].startMs, minutes, part: dayPart(sessions[i - 1].startMs, tz, opts) });
  }
  return out;
}

/**
 * Likely window for the next feed, relative to the latest feeding session.
 * Uses only records known at `nowMs`. The day/night group of the latest
 * feed is used when it alone meets the minimum; otherwise all intervals.
 */
export function computeFeedingPrediction(
  events: LocalEvent[],
  tz: string,
  nowMs: number,
  options: Partial<FeedingOptions> = {},
): FeedingPrediction {
  const opts = { ...DEFAULT_FEEDING_OPTIONS, ...options };
  const sessions = feedSessions(asOf(events, nowMs), opts.mergeWithinMin);
  const insufficient = (sampleSize: number, reason: string): FeedingPrediction => ({
    status: 'insufficient', modelVersion: FEED_MODEL_VERSION, sampleSize, required: opts.minIntervals, reason,
  });
  if (!sessions.length) return insufficient(0, 'No feeds recorded yet.');
  const basis = sessions[sessions.length - 1];
  const recent = feedIntervals(sessions, tz, opts).filter((i) => nowMs - i.toMs <= opts.lookbackDays * DAY_MS);
  if (recent.length < opts.minIntervals)
    return insufficient(recent.length, `Not enough data: ${recent.length} of ${opts.minIntervals} recent feed intervals.`);

  const part = dayPart(basis.startMs, tz, opts);
  const grouped = recent.filter((i) => i.part === part);
  const useGroup = grouped.length >= opts.minIntervals;
  const chosen = useGroup ? grouped : recent;
  const samples: Sample[] = chosen.map((i) => ({ value: i.minutes, weight: recencyWeight(nowMs - i.toMs, opts.halfLifeDays) }));
  const stats = windowStats(samples, { lowerQ: opts.lowerQ, upperQ: opts.upperQ, minSamples: opts.minIntervals });
  const window = toWindow(basis.startMs, stats);
  return {
    status: 'ok',
    modelVersion: FEED_MODEL_VERSION,
    basisEventId: basis.id,
    basisMs: basis.startMs,
    window,
    medianMin: stats.medianMin,
    sampleSize: chosen.length,
    group: useGroup ? part : 'all',
    widened: stats.widened,
    overdue: nowMs > window.endMs,
  };
}
